import React, { useEffect, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import styled, { css } from 'styled-components'
import { ReactComponent as DeleteIcon } from 'assets/images/delete.svg'
import { isEmpty } from 'ramda'

const AutocompleteInput = ({
  options = [],
  onChange = () => {},
  name = '',
  nomarginbottom = false,
  validate = () => {},
  label = '',
  value: initialValue, // = ''
  disabled = false,
  clearValueTrigger = '',
  additionalLabel = null,
  optional = false,
  withDelete = false,
  onDelete = () => {}
}) => {
  const [touched, _setTouched] = useState(false)
  const [value, _setValue] = useState(initialValue || '')
  const [{ valid, error }, _validate] = useState({
    valid: !validate,
    error: ''
  })
  useEffect(() => {
    validate && validate(name, v => {
      _validate({ valid: v.valid, error: v.errors })
    })
  }, [value])

  useEffect(() => {
    _setValue('')
  }, [clearValueTrigger])

  useEffect(() => {
    _setValue(initialValue)
  }, [initialValue])

  const handleBlur = () => {
    _setTouched(true)
  }

  const handleChange = (_, option) => {
    if (option) {
      _setValue(option.value)
      onChange(name, option.value)
    }
  }

  return (
    <InputWrapper nomarginbottom={nomarginbottom}>
      <LabelWrapper>
        <Label>
          {
            valid || (!valid && !touched) || disabled
              ? optional
              ? (
                <LabelWrapper>
                  {label}
                  <span>(optional)</span>
                </LabelWrapper>
              )
              : <div>{label}</div>
              : <Error>{error}</Error>
          }
          {additionalLabel && additionalLabel}
        </Label>
        {
          withDelete && (
            <DelIconWrapper onClick={onDelete}>
              <span>DELETE</span> <DeleteIcon />
            </DelIconWrapper>

          )
        }
      </LabelWrapper>
      <Autocomplete
        options={options}
        getOptionLabel={option => option.label}
        onChange={handleChange}
        getOptionDisabled={option => isEmpty(option.value)}
        value={value}
        disablePortal={false}
        PopperProps={{
          style: {
            zIndex: 100000
          },
          placement: 'bottom-start',
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 4]
              }
            },
            {
              name: 'preventOverflow',
              options: {
                padding: 8,
                boundary: 'viewport'
              }
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['bottom', 'top']
              }
            }
          ]
        }}
        slotProps={{
          popper: {
            sx: {
              '@media (max-width: 768px)': {
                width: '100% !important',
                '& .MuiAutocomplete-paper': {
                  width: '100% !important',
                  maxWidth: 'calc(100vw - 32px) !important',
                  margin: '4px 16px !important'
                }
              }
            }
          }
        }}
        ListboxProps={{
          style: {
            maxHeight: '300px'
          }
        }}
        renderInput={params => {
          return (
            <TextField
              id={params.id}
              inputProps={params.inputProps}
              error={(!valid && touched) && !disabled}
              validate={validate}
              InputProps={{
                ...params.InputProps,
                style: {
                  height: '45px',
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: disabled ? '#fcfcfc' : '#fff',
                }
              }}
              handleBlur={handleBlur}
              variant='outlined'
              style={{
                width: '100%'
              }}
            />
          )
        }}
      />
    </InputWrapper>
  )
}

export default AutocompleteInput

const InputWrapper = styled.div`
  position: relative;
  margin-bottom: ${({ nomarginbottom }) => nomarginbottom ? 0 : '25px' };
  width: 100%;
  box-sizing: border-box;

  .MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root'] .MuiAutocomplete-input:first-child {
    padding: 0;
  }
  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors.secondary};
  }

  .MuiAutocomplete-root {
    width: 100%;
  }

  .MuiAutocomplete-inputRoot {
    width: 100% !important;
    box-sizing: border-box;
  }

  .MuiOutlinedInput-root {
    width: 100% !important;
    margin: 0 !important;
    box-sizing: border-box;

    @media (max-width: 768px) {
      border-radius: 6px;
    }
  }

  .MuiAutocomplete-popper {
    @media (max-width: 768px) {
      width: 100% !important;
      max-width: 100% !important;
    }
  }

  .MuiAutocomplete-paper {
    @media (max-width: 768px) {
      width: 100% !important;
      max-width: calc(100vw - 32px) !important;
      margin: 4px 0 !important;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
      max-height: 300px !important;
      overflow-y: auto;
    }
  }

  .MuiAutocomplete-paper {
    @media (max-width: 768px) {
      width: 100% !important;
      max-width: 100% !important;
      margin: 4px 0 !important;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
      max-height: 300px !important;
      overflow-y: auto;
    }
  }

  .MuiAutocomplete-listbox {
    @media (max-width: 768px) {
      padding: 8px !important;
    }
  }

  .MuiAutocomplete-option {
    @media (max-width: 768px) {
      padding: 12px 16px !important;
      margin: 4px 0 !important;
      border-radius: 6px;
      min-height: 48px;
    }
  }
`

const Label = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 7px;
  font-weight: bold;
  font-size: 12px;
  letter-spacing: .11px;
  min-height: 17px;
`

const Error = styled.span`
  color: ${({ theme }) => theme.colors.error};
`

const LabelWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  align-items: center;

  ${({ withDelete }) => withDelete && css`
    color: ${({ theme }) => theme.colors.primary};
  `}
`

const DelIconWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 7px;
  cursor: pointer;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};

  span {
    transition: all .3s;
    opacity: 0;
  }

  &:hover {
    span {
      opacity: 1;
    }
  }
`
